<template>
  <div>
    <vx-card>
      <p class="mb-5">
        Information in this section (Introduction) does not need to be learned or memorised and you
        will not be quizzed on it.
      </p>
      <p>
        This information is provided for you to read through and gain an understanding of this
        particular career path before starting your Virtual Job Experience.
      </p>
    </vx-card>
    <vx-card class="mt-base mb-10">
      <h4 class="mb-5">Let’s start your Games Developer/Designer work experience!</h4>
      <p class="mb-5">
        <strong>Software and Applications Programmers</strong> design, develop, test, maintain and
        document program code in accordance with user requirements, and system and technical
        specifications.
      </p>

      <heading size="h3" style-size="h5" class="mb-5 font-normal"><u>Education Level:</u></heading>
      <p class="mb-5">
        You usually need a bachelor or postgraduate degree in a related information technology field
        (such as programming, software engineering, software development, or computer science) to
        work as a Software or Applications Programmer. Some workers have a Vocational Education and
        Training (VET) qualification.
      </p>

      <p class="mb-5">
        Before starting a course, check it will provide you with the skills and qualifications you
        need.
      </p>
      <p class="mb-5">Visit:</p>

      <p class="mb-5">
        <a href="https://www.courseseeker.edu.au/" target="_blank">Course Seeker</a> is a useful
        resource to search and compare higher education courses.
      </p>
      <p class="mb-5">
        <a href="https://www.compared.edu.au/" target="_blank">ComparED</a> to compare undergraduate
        and postgraduate student experiences and outcomes.
      </p>
      <p class="mb-5">
        <a href="https://www.myskills.gov.au/" target="_blank">My Skills</a> to compare Vocational
        Education and Training (VET) courses, providers, and student outcomes.
      </p>
      <p class="mb-10">
        <a href="https://www.aapathways.com.au/career-research/job-pathways" target="_blank"
          >AAPathways</a
        >
        website to explore Information and Communications Technology VET training pathways.
      </p>
    </vx-card>

    <vx-card>
      <h4 class="mb-5">Quick Details:</h4>

      <div class="vx-row mt-5 flex justify-center">
        <div class="vx-col xl:w-2/3 lg:w-1/2 w-full my-5">
          <h4 class="mb-5">Software and Applications Programmer</h4>

          <ul class="default-list">
            <li><strong>Size:</strong> This is a very large occupation.</li>
            <li>
              <strong>Earnings:</strong> Full-time workers on an adult wage earn around $2,003 per
              week (higher than the average of $1,460). Earnings tend to be lower when starting and
              higher as experience grows.
            </li>
            <li>
              <strong>Full-time:</strong> Most work full-time (90%, much higher than the average of
              66%).
            </li>
            <li>
              <strong>Hours:</strong> Full-time workers spend around 41 hours per week at work
              (compared to the average of 44 hours).
            </li>
            <li>
              <strong>Age:</strong> The average age is 37 years (compared to the average of 40
              years).
            </li>
            <li>
              <strong>Gender:</strong> 17% of workers are female (compared to the average of 48%).
            </li>
          </ul>

          <h4 class="mb-5">Tasks</h4>
          <ul class="default-list">
            <li>Researching, consulting, analysing, and evaluating system program needs</li>
            <li>
              Identifying technology limitations and deficiencies in existing systems and associated
              processes, procedures, and methods
            </li>
            <li>
              Testing, debugging, diagnosing, and correcting errors and faults in an applications
              programming language within established testing protocols, guidelines, and quality
              standards to ensure programs and applications perform to specification
            </li>
            <li>
              Writing and maintaining program code to meet system requirements, system designs, and
              technical specifications in accordance with quality accredited standards
            </li>
            <li>
              Writing, updating, and maintaining technical program, end-user documentation, and
              operational procedures
            </li>
            <li>
              Providing advice, guidance, and expertise in developing proposals and strategies for
              software design activities such as financial evaluation and costings for recommending
              software purchases and upgrades
            </li>
          </ul>
        </div>
        <div class="vx-col xl:w-1/3 lg:w-1/2 w-full my-5">
          <img
            src="@/assets/images/experiences/gamesdeveloper/jo1.png"
            class="img-responsive"
            alt="Software and Applications Programmers job outlook: $2,003 weekly pay, very strong future growth, 135,000 workers, very high skill level, 90% full-time, 41 hours average full-time week, average age 37, 17% female."
          />
          <p class="mt-5">
            Source: <a href="https://joboutlook.gov.au/" target="_blank">Job Outlook</a>
          </p>
        </div>
      </div>
      <vs-alert color="dark" title="Caution" active="true">
        The Australian jobs market is changing in response to the COVID-19 pandemic. These estimates
        do not take account of the impact of COVID-19. They may not reflect the current jobs market
        and should be used and interpreted with extreme caution.
      </vs-alert>
    </vx-card>

    <vx-card>
      <p class="mb-5 text-bold">
        As we can see from the above overview, there is a huge range of opportunities in this area,
        with many different types of jobs and opportunities available. While these jobs often share
        some similar base skills, you can pursue many different specialisations based on your
        particular talents and interests.
      </p>
      <p class="mb-5">Let’s take a look at one of these jobs:</p>
      <p class="mb-5 text-bold">Games Developer</p>
      <centered-image-wrapper :image="image1" alt="" />

      <p class="mb-5 text-bold text-italic">
        Software developers and engineers may be involved in all aspects of a game's creation, from
        concept, story writing, coding and programming, production, audio, design, and visual arts.
      </p>
      <p class="mb-5 text-bold">TAFE:</p>
      <ul class="default-list">
        <li>Diploma in Games Design, Digital, and Interactive Games</li>
        <li>
          Certificate in programming languages such as C++, Java, Maya Scripting Language (MEL)
        </li>
      </ul>

      <p class="mb-5 text-bold">Undergraduate:</p>
      <ul class="default-list">
        <li>Bachelor of Computer Science</li>
        <li>Bachelor of Science in Games Development</li>
        <li>Bachelor of Game Design</li>
        <li>Bachelor of Digital Media</li>
      </ul>
      <p class="mb-5">
        <strong>Skills:</strong> Programming, Digital Storytelling, and Storyboard, 2D/3D modelling,
      </p>
      <p class="mb-5"><strong>Salary:</strong> 85k - 120k</p>
      <p class="mb-5"><strong>Tasks:</strong></p>
      <ul class="default-list">
        <li>
          Use your artistic skills to design engaging and user-friendly digital content and apps
        </li>
        <li>Use your coding and engineering skills to create software and hardware products</li>
        <li>Use your IT and business knowledge to market solutions to clients and industry.</li>
        <li>Use your deep technical skills to securely manage and interpret clients’ data.</li>
      </ul>
      <p class="mb-5">
        [Source:
        <a href="https://www.film.vic.gov.au/" target="_blank"
          >https://www.careersfoundation.com.au/gamesdeveloper</a
        >]
      </p>
      <p class="mb-5">
        <strong>Did you know?</strong> According to <a href="" target="_blank">Film Victoria</a>,
        Victoria is home to half of Australia's digital games industry, with more than 100 game
        development studios, animation houses and industry service providers.
      </p>
    </vx-card>
  </div>
</template>

<script>
export default {
  data() {
    return {
      popupActive: false,
      image1: require('@/assets/images/experiences/gamesdeveloper/intro1.png'),
    };
  },
};
</script>
